import {
  type Auth,
  clearAuth,
  getAuth,
  setAuth,
  setSignOutNotice,
} from './auth'
import { getApiUrl } from './utils'

export interface PaginationMeta {
  limit: number
  offset: number
  total: number
  hasMore: boolean
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

// The API wraps every answer in { meta, data, pagination? }.
async function send(method: string, path: string, body?: unknown, auth = true) {
  const response = await fetch(`${getApiUrl()}${path}`, {
    method,
    headers: {
      ...(auth ? { Authorization: `Bearer ${getAuth()?.accessToken}` } : {}),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  const json = await response.json().catch(() => ({}))
  return { response, json }
}

async function refresh(): Promise<boolean> {
  const auth = getAuth()
  if (!auth) {
    return false
  }
  const { response, json } = await send(
    'POST',
    '/auth/refresh',
    { refreshToken: auth.refreshToken },
    false,
  )
  if (!response.ok) {
    return false
  }
  setAuth({ ...auth, accessToken: json.data.accessToken })
  return true
}

// Ends the session; the reload lands on the sign-in page, which shows why.
function signOut(reason: string) {
  clearAuth()
  setSignOutNotice(reason)
  window.location.reload()
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  auth = true,
) {
  let { response, json } = await send(method, path, body, auth)
  // An access token lives an hour. Renew it once; a refresh that fails, or a
  // renewed token that is refused too, means the session is over.
  if (response.status === 401 && auth) {
    if (await refresh()) {
      ;({ response, json } = await send(method, path, body, auth))
    }
    if (response.status === 401) {
      signOut('Your session has ended. Sign in again.')
    }
  }
  // Every route lab calls is for admins: a 403 means this user is not one
  // (any more). Staying signed in would only show errors.
  if (response.status === 403 && auth) {
    signOut(json.error?.message ?? 'Lab is for admin users.')
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      json.error?.message ?? json.message ?? response.statusText,
    )
  }
  return json as { data: T; pagination?: PaginationMeta }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path).then((r) => r.data),
  getPaginated: <T>(path: string) => request<T[]>('GET', path),
  post: <T>(path: string, body?: unknown, auth = true) =>
    request<T>('POST', path, body, auth).then((r) => r.data),
  put: <T>(path: string, body?: unknown) =>
    request<T>('PUT', path, body).then((r) => r.data),
  delete: <T>(path: string) => request<T>('DELETE', path).then((r) => r.data),
}

export type { Auth }
